import { create } from 'zustand';
import { leerVehiculo } from '../constants/vehiculoDeFlota';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { firestoreService } from '../services/firestoreService';
import type { UserRole as BaseUserRole } from '../types/roles';

/**
 * ALMACÉN A PRUEBA DEL RENDERIZADO EN NODE.
 *
 * `expo export -p web` renderiza cada pantalla en Node para generar el HTML
 * estático. Allí no existe `window`, y en web AsyncStorage no es más que
 * `window.localStorage`.
 *
 * La LECTURA ya estaba protegida — el fallo se recoge abajo, en
 * `onRehydrateStorage`, y la app arranca limpia. La ESCRITURA no lo estaba:
 * `setHasHydrated(true)` hace que el middleware guarde el estado, `setItem`
 * toca `window` y revienta con `ReferenceError: window is not defined`.
 *
 * Y no revienta una pantalla: **revienta la exportación entera**, que termina
 * sin generar ni un archivo. Eso es lo que llevaba desde el 14 de agosto
 * impidiendo volver a publicar la web — con la consecuencia de que el sitio
 * seguía sirviendo la Política de Privacidad vieja, la que declaraba
 * procesamiento biométrico por AWS Rekognition, en la URL que abre el revisor
 * de Google Play desde la ficha de la tienda.
 *
 * Guardar mientras se genera el HTML no tendría sentido de todas formas: ese
 * proceso no es la sesión de nadie y su estado no le sirve a ningún usuario.
 * Así que en Node no se guarda nada y se sigue adelante.
 *
 * En React Native `window` SÍ existe (lo define el propio runtime), así que en
 * el teléfono esto se comporta exactamente igual que antes.
 */
const puedeGuardar = () => typeof window !== 'undefined';

const almacenSeguro = {
  getItem: async (nombre: string): Promise<string | null> => {
    if (!puedeGuardar()) return null;
    return AsyncStorage.getItem(nombre);
  },
  setItem: async (nombre: string, valor: string): Promise<void> => {
    if (!puedeGuardar()) return;
    await AsyncStorage.setItem(nombre, valor);
  },
  removeItem: async (nombre: string): Promise<void> => {
    if (!puedeGuardar()) return;
    await AsyncStorage.removeItem(nombre);
  },
};

/**
 * Detección automática del idioma según el device locale.
 *
 * Reglas:
 *   - languageCode 'ht' → Kreyòl ayisyen (Haití)
 *   - languageCode 'en' → English (US/UK/CA/...)
 *   - regionCode 'BR' → portugués? NO — el modelo soporta solo es/en/ht
 *   - Cualquier otro → español (R.D., LATAM, ES) por default
 *
 * Esta función se ejecuta SOLO una vez al inicializar el store. Si el
 * usuario después cambia manualmente el idioma desde Perfil, ese cambio
 * se persiste y `languageManuallySet=true` evita que la detección
 * automática sobrescriba la elección del usuario.
 *
 * Para SSR/web sin device locale → 'es' default.
 */
function detectInitialLanguage(): 'es' | 'en' | 'ht' {
  try {
    // Importación lazy para no fallar en SSR
    const Localization = require('expo-localization');
    const locales = Localization.getLocales?.() ?? [];
    const first = locales[0];
    if (!first) return 'es';
    const langCode = (first.languageCode ?? '').toLowerCase();
    const region = (first.regionCode ?? '').toUpperCase();

    if (langCode === 'ht' || region === 'HT') return 'ht';
    if (langCode === 'en') return 'en';
    return 'es';
  } catch {
    return 'es';
  }
}

// El store admite `null` (sesión sin rol resuelto) además de los roles válidos.
export type UserRole = BaseUserRole | null;
export type ThemeMode = 'light' | 'dark';

export interface Student {
  id: string;
  name: string;
  school: string;
  photoUri?: string;
}

export interface RentCarReminder {
  id: string;
  type: 'PAGO' | 'MANTENIMIENTO';
  title: string;
  subtitle: string;
  amount?: number;
}

interface AppState {
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
  userRole: UserRole;
  baseRole: UserRole; // El rol original del usuario (para el botón de "Volver a Rent Car" etc)
  isDriverVerified: boolean; // Si ya subió los documentos de chofer
  students: Student[];
  isLoggedIn: boolean;
  theme: ThemeMode;
  /**
   * Radio en km dentro del cual el chofer considera que un viaje es «de su
   * zona». NO esconde nada: solo decide por cuales SUENA el telefono y
   * cuales se rotulan «Lejos de ti». Con un solo chofer disponible,
   * esconder un viaje es perderlo — ver la nota larga en map.tsx.
   *
   * 6 es el valor que llevaba clavado el servicio para todo el pais
   * (firestoreService.ts:1162); se deja igual para que nadie cambie de
   * comportamiento sin tocar el control.
   */
  radioZonaKm: number;
  /** Cuantas solicitudes ve el chofer ahora mismo. Lo escribe map.tsx para
   *  que Actividad lo pinte sin abrir un segundo oyente. */
  solicitudesVisibles: number;
  isFirstRideFreeUsed: boolean;
  isVerified: boolean;
  isProfilePhotoTaken: boolean;
  userPhoto: string | null;
  userName: string;
  userPhone: string;
  userEmail: string;
  userFirebaseUid: string | null;
  // Datos del vehículo del chofer — Uber-style. El pasajero necesita saber
  // qué carro esperar: marca, modelo, color y placa. Se capturan en KYC-docs
  // y se propagan en cada oferta de viaje. Se muestran en RideOfferCard y
  // ActiveRideCard debajo del nombre del chofer.
  vehicleBrand: string | null;    // "Toyota"
  vehicleModel: string | null;    // "Corolla"
  vehicleColor: string | null;    // "Blanco"
  vehicleYear: string | null;     // "2020"
  licensePlate: string | null;    // "A123456"
  setVehicleInfo: (info: {
    brand?: string | null;
    model?: string | null;
    color?: string | null;
    year?: string | null;
    plate?: string | null;
  }) => void;
  rentCarVehicles: any[];
  rentCarDrivers: any[];
  rentCarCandidates: any[];
  rentCarTrips: any[];
  rentCarReminders: RentCarReminder[];
  activeChatPartner: { name: string; vehicle: string; role: 'DRIVER' | 'CANDIDATE'; ownerId?: string; partnerUid?: string } | null;
  setRole: (role: UserRole) => void;
  setBaseRole: (role: UserRole) => void;
  setDriverVerified: (status: boolean) => void;
  setLogin: (status: boolean) => void;
  setTheme: (theme: ThemeMode) => void;
  setRadioZonaKm: (km: number) => void;
  setSolicitudesVisibles: (n: number) => void;
  toggleRole: () => void;
  markFirstRideUsed: () => void;
  addStudent: (student: Omit<Student, 'id'>) => void;
  setVerified: (status: boolean) => void;
  /**
   * El servidor dice que esta persona ya agotó sus viajes de cortesía y tiene
   * que verificar su cédula para seguir pidiendo viajes.
   *
   * ⚠️ Esto NO es el candado — el candado está en firestore.rules
   * (`puedePedirViajeSinVerificar`). Esto es para que la pantalla pueda
   * avisarle ANTES de que toque el botón, en vez de devolverle un
   * `permission-denied` mudo. El campo lo escribe solo el servidor
   * (`processRideCompletion`, functions/src/loyalty.ts) y está blindado contra
   * el cliente en las reglas.
   */
  requiereVerificacion: boolean;
  viajesSinVerificar: number;
  viajesSinVerificarTope: number;
  setEstadoDeVerificacion: (d: { requiere: boolean; hechos: number; tope: number }) => void;
  /**
   * Sube la bandera y NUNCA la baja.
   *
   * Existe por una carrera real: en cada arranque corren dos cosas a la vez
   * — la lectura del perfil y `registrarDispositivo`, que es la que calcula
   * el veredicto y lo escribe. Si la lectura del perfil gana, trae el campo
   * viejo (o vacío) y, con un setter normal, pisaría el veredicto recién
   * calculado. Justo en el arranque que importa: el del correo nuevo en un
   * teléfono ya agotado.
   *
   * Bajarla no hace falta desde aquí: el servidor la limpia al aprobar la
   * cédula y el próximo arranque la lee ya en false.
   */
  subirRequiereVerificacion: (d: { hechos: number; tope: number }) => void;
  setProfilePhotoTaken: (status: boolean) => void;
  setUserPhoto: (photo: string | null) => void;
  setProfile: (name: string, phone: string, email: string) => void;
  setUserFirebaseUid: (uid: string | null) => void;

  // Dirección de entrega activa (food delivery)
  selectedDeliveryAddress: {
    id: string;
    label: string;
    line1: string;
    lat?: number | null;
    lng?: number | null;
  } | null;
  setSelectedDeliveryAddress: (addr: AppState['selectedDeliveryAddress']) => void;

  language: 'es' | 'en' | 'ht';
  languageManuallySet: boolean;
  setLanguage: (lang: 'es' | 'en' | 'ht') => void;

  // QA — Modo simulado del flow de viaje. Cuando está en TRUE, el botón
  // "Solicitar Viaje" NO toca Firestore y simula localmente un chofer demo.
  // Por defecto está en FALSE para que las pruebas con dispositivos reales
  // se conecten entre sí vía Firestore (pasajero ↔ chofer en distintos
  // móviles). Se enciende solo desde Perfil → QA cuando un solo tester
  // quiere ver el flujo sin coordinar con otra persona.
  qaMockRide: boolean;
  setQaMockRide: (enabled: boolean) => void;

  /**
   * MODO VISTA PREVIA (invitado sin cuenta).
   *
   * NO es un rol. Es una bandera aparte, y esa distinción es el punto clave:
   * antes "invitado" se guardaba como `userRole = 'GUEST'`, y como el código
   * tiene ~137 comparaciones `role === 'X'`, el invitado caía siempre en el
   * `else` de cada una. Resultado: veía el Panel de Ganancias del chofer y
   * "Modo Conductor" por descarte, aunque hubiera entrado como pasajero.
   *
   * Ahora `userRole` guarda el rol REAL que está explorando (PASSENGER,
   * DRIVER, RENT_CAR_OWNER…) y esta bandera responde a otra pregunta
   * distinta: ¿tiene cuenta o no? Así las 137 comparaciones siguen
   * funcionando sin tocar ninguna.
   *
   * Se bloquean las ACCIONES, nunca la NAVEGACIÓN: el invitado — y el
   * revisor de Google — debe poder recorrer cualquier rol completo.
   */
  guestPreview: boolean;
  setGuestPreview: (enabled: boolean) => void;
  /** Devuelve el error del servidor, o null si se guardo de verdad. */
  addRentCarVehicle: (vehicle: any) => Promise<Error | null>;
  removeRentCarVehicle: (id: string) => void;
  toggleVehiclePublish: (id: string, isPublished: boolean) => void;
  approveCandidateDriver: (id: string, name: string) => void;
  rejectCandidateDriver: (id: string) => void;
  resolveReminder: (id: string) => void;
  setActiveChatPartner: (partner: { name: string; vehicle: string; role: 'DRIVER' | 'CANDIDATE'; ownerId?: string; partnerUid?: string } | null) => void;
  kycDocumentUri: string | null;
  setKycDocumentUri: (uri: string | null) => void;
  // Base64 del FRENTE de la cédula — capturado al tomar la foto en
  // kyc-documents para subirlo a Firebase Storage sin pasar por FileReader
  // (que en RN 0.85+Hermes lanza "Creating blobs from ArrayBuffer not
  // supported"). ImagePicker base64:true lo devuelve listo.
  kycDocumentBase64: string | null;
  setKycDocumentBase64: (b64: string | null) => void;

  // Wallet & Rewards
  walletBalance: number;
  /**
   * Días que le quedan de prueba al chofer. `null` = TODAVÍA NO SE SABE.
   * Antes arrancaba en 14 y ese 14 se pintaba como un hecho a roles que ni
   * siquiera pagan cuota (restaurante, rent car, escolar…), y seguía diciendo
   * 14 el mes siguiente. Un número inventado en pantalla es una promesa.
   */
  driverTrialDaysLeft: number | null;
  driverTripsThisMonth: number;
  driverTotalLifetimeTrips: number;
  driverBonusAccrued: number;
  driverPaymentAccount: string | null;
  currentDayOfMonth: number;
  passengerTripsCompleted: number; // Viajes pagados del pasajero
  freeTripsAvailable: number; // Cupones de viajes gratis
  
  // School Transport Status
  schoolDriverStatus: 'SEARCHING' | 'TRIAL' | 'ACTIVE';
  schoolTrialDaysLeft: number;
  
  setWalletBalance: (balance: number) => void;
  setDriverPaymentAccount: (account: string | null) => void;
  processBonusWithdrawal: () => void;
  processBonusToSubscription: () => void;
  
  // Realtime actions
  setRentCarDrivers: (drivers: any[]) => void;
  setRentCarVehicles: (vehicles: any[]) => void;
  setRentCarCandidates: (candidates: any[]) => void;
  incrementDriverTrips: () => void;
  incrementPassengerTrips: () => void;
  addFreeTrip: () => void;
  useFreeTrip: () => void;
  // FIX A-5 — Limpia TODO el estado de sesión/usuario al cerrar sesión, para
  // que al cambiar de cuenta no se arrastren rol, driverStatus, saldo, etc.
  resetSession: () => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
  _hasHydrated: false,
  setHasHydrated: (state) => set({ _hasHydrated: state }),
  userRole: null,
  baseRole: null,
  isDriverVerified: false,
  students: [],
  isLoggedIn: false,
  theme: 'light',
  radioZonaKm: 6,
  solicitudesVisibles: 0,
  isFirstRideFreeUsed: false,
  isVerified: false,
  isProfilePhotoTaken: false,
  userPhoto: null,
  userName: '',
  userPhone: '',
  userEmail: '',
  userFirebaseUid: null,
  vehicleBrand: null,
  vehicleModel: null,
  vehicleColor: null,
  vehicleYear: null,
  licensePlate: null,
  setVehicleInfo: (info) => set((state) => ({
    vehicleBrand: info.brand !== undefined ? info.brand : state.vehicleBrand,
    vehicleModel: info.model !== undefined ? info.model : state.vehicleModel,
    vehicleColor: info.color !== undefined ? info.color : state.vehicleColor,
    vehicleYear: info.year !== undefined ? info.year : state.vehicleYear,
    licensePlate: info.plate !== undefined ? info.plate : state.licensePlate,
  })),
  rentCarVehicles: [],
  rentCarDrivers: [],
  rentCarCandidates: [],
  rentCarTrips: [],
  rentCarReminders: [],
  activeChatPartner: null,
  kycDocumentUri: null,
  kycDocumentBase64: null,

  // Initial Wallet & Rewards
  walletBalance: 0,
  driverTrialDaysLeft: null,
  driverTripsThisMonth: 0,
  driverTotalLifetimeTrips: 0,
  driverBonusAccrued: 0,
  driverPaymentAccount: null,
  currentDayOfMonth: new Date().getDate(),
  passengerTripsCompleted: 0,
  freeTripsAvailable: 0,
  
  schoolDriverStatus: 'SEARCHING',
  schoolTrialDaysLeft: 15,

  // QA Mock Ride — opt-in explícito desde Perfil
  qaMockRide: false,
  setQaMockRide: (enabled) => set({ qaMockRide: enabled }),

  guestPreview: false,
  setGuestPreview: (enabled) => set({ guestPreview: enabled }),

  setRole: (role) => set({ userRole: role }),
  setBaseRole: (role) => set({ baseRole: role }),
  setKycDocumentUri: (uri) => set({ kycDocumentUri: uri }),
  setKycDocumentBase64: (b64) => set({ kycDocumentBase64: b64 }),
  setDriverVerified: (status) => set({ isDriverVerified: status }),
  setLogin: (status) => set({ isLoggedIn: status }),
  setTheme: (theme) => set({ theme }),
  // Se acota a lo que el control ofrece: un valor raro llegado de un store
  // viejo o de un toque fantasma no puede dejar al chofer sin avisos.
  setRadioZonaKm: (km) => set({ radioZonaKm: Number.isFinite(km) && km > 0 ? Math.min(km, 50) : 6 }),
  setSolicitudesVisibles: (n) => set({ solicitudesVisibles: Number.isFinite(n) && n > 0 ? n : 0 }),
  setVerified: (status) => set({ isVerified: status }),
  requiereVerificacion: false,
  viajesSinVerificar: 0,
  viajesSinVerificarTope: 5,
  setEstadoDeVerificacion: (d) =>
    set({
      requiereVerificacion: d.requiere,
      viajesSinVerificar: d.hechos,
      viajesSinVerificarTope: d.tope,
    }),
  subirRequiereVerificacion: (d) =>
    set({
      requiereVerificacion: true,
      viajesSinVerificar: d.hechos,
      viajesSinVerificarTope: d.tope,
    }),
  setProfilePhotoTaken: (status) => set({ isProfilePhotoTaken: status }),
  setUserPhoto: (photo) => set({ userPhoto: photo }),
  setProfile: (name, phone, email) => set({ userName: name, userPhone: phone, userEmail: email }),
  setUserFirebaseUid: (uid) => set({ userFirebaseUid: uid }),

  // FIX A-5 — Reset de sesión al logout. Preserva SOLO prefs de dispositivo
  // (theme, language); borra todo lo específico del usuario.
  //
  // ⚠️ `qaMockRide` YA NO SE PRESERVA, y el comentario de antes lo listaba como
  // preferencia del aparato. Dejó de serlo el día que el viaje simulado pasó de
  // herramienta de QA a MODO DEMOSTRACIÓN público: ahora cualquiera puede
  // encenderlo, y quedarse con él puesto significa que la siguiente solicitud
  // de viaje sería de mentira. Que se lo quede la persona que entra después en
  // ese mismo teléfono es justo el fallo que no puede ocurrir.
  //
  // (Nunca llegó a sobrevivir a cerrar la app, porque no está en `partialize`
  // y por tanto no se escribe en disco. Pero sí sobrevivía al cambio de
  // usuario dentro de la misma sesión, que es lo que esto cierra.)
  //
  // ESTO ES TAMBIÉN LA LIMPIEZA DEL BORRADO DE CUENTA, no solo la del logout.
  // Este store está PERSISTIDO en disco (`persist` + AsyncStorage, clave
  // `go-safely-storage-v2`), así que lo que no se limpie aquí sobrevive a
  // desinstalar la sesión: nombre, teléfono, correo, foto y placa se quedaban
  // en el teléfono y los veía la siguiente persona que abriera la app, aunque
  // el servidor hubiera borrado la cuenta entera. La app prometía "borramos
  // tus datos personales" y los dejaba en el dispositivo.
  //
  // REGLA AL EDITAR: toda clave que se añada a `partialize` (abajo) tiene que
  // aparecer también en esta lista, salvo que sea una preferencia del APARATO
  // y no de la persona (tema, idioma). Si no, vuelve la fuga.
  resetSession: () => set({
    userRole: null,
    baseRole: null,
    // Cerrar sesión NO es lo mismo que explorar sin cuenta: al salir se apaga
    // la vista previa y el usuario vuelve al selector normal.
    guestPreview: false,
    isDriverVerified: false,
    students: [],
    isLoggedIn: false,
    isFirstRideFreeUsed: false,
    isVerified: false,
    isProfilePhotoTaken: false,
    userPhoto: null,
    userName: '',
    userPhone: '',
    userEmail: '',
    userFirebaseUid: null,
    vehicleBrand: null,
    vehicleModel: null,
    vehicleColor: null,
    vehicleYear: null,
    licensePlate: null,
    activeChatPartner: null,
    kycDocumentUri: null,
    kycDocumentBase64: null,
    walletBalance: 0,
    driverTrialDaysLeft: null,
    driverTripsThisMonth: 0,
    driverTotalLifetimeTrips: 0,
    driverBonusAccrued: 0,
    driverPaymentAccount: null,
    passengerTripsCompleted: 0,
    freeTripsAvailable: 0,
    schoolDriverStatus: 'SEARCHING',
    schoolTrialDaysLeft: 15,
    selectedDeliveryAddress: null,
    rentCarVehicles: [],
    rentCarDrivers: [],
    rentCarCandidates: [],
    rentCarTrips: [],
    rentCarReminders: [],
    // Ver la nota de arriba: el modo demostración se apaga al salir.
    qaMockRide: false,
  }),

  selectedDeliveryAddress: null,
  setSelectedDeliveryAddress: (addr) => set({ selectedDeliveryAddress: addr }),

  language: detectInitialLanguage(),
  languageManuallySet: false as boolean,
  setLanguage: (lang: 'es' | 'en' | 'ht') => set({ language: lang, languageManuallySet: true } as any),

  // Actions Wallet & Rewards
  setWalletBalance: (balance) => set({ walletBalance: balance }),
  setDriverPaymentAccount: (account) => set({ driverPaymentAccount: account }),
  processBonusWithdrawal: () => set({ driverBonusAccrued: 0 }),
  processBonusToSubscription: () => set((state) => ({ 
    walletBalance: state.walletBalance + state.driverBonusAccrued, 
    driverBonusAccrued: 0 
  })),

  // Realtime Actions
  setRentCarDrivers: (drivers) => set({ rentCarDrivers: drivers }),
  setRentCarVehicles: (vehicles) => set({ rentCarVehicles: vehicles }),
  setRentCarCandidates: (candidates) => set({ rentCarCandidates: candidates }),

  incrementDriverTrips: () => set((state) => ({ 
    driverTripsThisMonth: state.driverTripsThisMonth + 1,
    driverTotalLifetimeTrips: state.driverTotalLifetimeTrips + 1
  })),
  incrementPassengerTrips: () => set((state) => {
    const newTrips = state.passengerTripsCompleted + 1;
    if (newTrips >= 10) {
      return { passengerTripsCompleted: 0, freeTripsAvailable: state.freeTripsAvailable + 1 };
    }
    return { passengerTripsCompleted: newTrips };
  }),
  addFreeTrip: () => set((state) => ({ freeTripsAvailable: state.freeTripsAvailable + 1 })),
  useFreeTrip: () => set((state) => ({ 
    freeTripsAvailable: Math.max(0, state.freeTripsAvailable - 1) 
  })),
  
  // Las 3 acciones de vehículos hacen pass-through a Firestore. El listener
  // en (tabs)/_layout es la fuente única de verdad y reconcilia rentCarVehicles
  // en el siguiente tick. La mutación local optimista es solo para feedback
  // visual mientras llega el snapshot.
  // ══════════════════════════════════════════════════════════════════════
  // EL CARRO QUE SE GUARDABA EN NINGUNA PARTE
  // ══════════════════════════════════════════════════════════════════════
  // El fundador: «cuando se le da agregar, el carro no hace nada».
  //
  // El fallo no era que no escribiera: era que no MIRABA si habia escrito. El
  // `catch` mandaba el error a `console.error` —que en un telefono no lo ve
  // nadie— y la pantalla seguia adelante y sacaba «Éxito: vehículo agregado».
  // La regla de /vehicles (firestore.rules) exige tres cosas: ser
  // RENT_CAR_OWNER EN EL SERVIDOR, tener el KYC aprobado y que la placa tenga
  // forma valida. Si falla cualquiera, el carro aparecia en la lista hasta
  // cerrar la app y luego desaparecia, sin que nadie dijera por que.
  //
  // Ahora: se mete en la lista para que se vea al instante, pero si el servidor
  // lo rechaza se SACA de la lista y se devuelve el error para que la pantalla
  // lo diga. Una lista que miente es peor que una vacia.
  addRentCarVehicle: async (vehicle) => {
    const state = useAppStore.getState();
    set({ rentCarVehicles: [vehicle, ...state.rentCarVehicles] });
    const quitarDeLaLista = () => {
      const ahora = useAppStore.getState().rentCarVehicles;
      set({ rentCarVehicles: ahora.filter((v: any) => v?.id !== vehicle?.id) });
    };
    const ownerId = state.userFirebaseUid;
    if (!ownerId) {
      quitarDeLaLista();
      return new Error('No hay sesión. Cierra la app y vuelve a entrar.');
    }
    if (ownerId) {
      try {
        await firestoreService.addVehicle(ownerId, vehicle);
      } catch (e: any) {
        console.error('addRentCarVehicle no sincronizó con Firestore:', e);
        quitarDeLaLista();
        return e instanceof Error ? e : new Error(String(e?.message ?? e));
      }
    }
    // Guardado de verdad: el carro se queda en la lista.
    return null;
  },
  removeRentCarVehicle: (id) => {
    set((state) => ({
      rentCarVehicles: state.rentCarVehicles.filter(v => v.id !== id),
    }));
    // Borrado físico: por ahora no exponemos delete en firestoreService para no
    // romper datos históricos. El listener traerá la lista sin el doc si lo borras
    // desde la consola; mientras tanto el filtro local oculta el item.
  },
  toggleVehiclePublish: (id, isPublished) => {
    set((state) => ({
      rentCarVehicles: state.rentCarVehicles.map(v => v.id === id ? { ...v, isPublished } : v),
    }));
    firestoreService.toggleVehiclePublishStatus(id, isPublished).catch((e) => {
      console.error('toggleVehiclePublish no sincronizó con Firestore:', e);
    });
  },
  approveCandidateDriver: (id, name) => {
    // Optimista: quitamos el candidato de la lista local de inmediato para feedback visual.
    // La lista oficial de drivers viene del listener Firestore en (tabs)/_layout — no la
    // mutamos local para no crear doble fuente de verdad.
    const state = useAppStore.getState();
    const availableVehicle = state.rentCarVehicles.find(v => v.status === 'Disponible');
    const assignedVehicleName = availableVehicle
      // ⚠️ Con un carro dado de alta por el formulario del panel esto
      // escribía «undefined undefined (undefined)» EN LA FICHA DEL CHOFER, que
      // es justo donde el dueño mira qué carro lleva cada uno. Ese formulario
      // guardaba `brand` en vez de `make` y no guardaba `year`.
      ? (() => {
          const c = leerVehiculo(availableVehicle);
          return c.year ? `${c.titulo} (${c.year})` : c.titulo;
        })()
      : 'Sin Vehículo Asignado';

    set({
      rentCarCandidates: state.rentCarCandidates.filter(c => c.id !== id),
    });

    // Sincronización con Firestore. Sin uid no escribimos para no contaminar otros owners.
    const ownerId = state.userFirebaseUid;
    if (ownerId) {
      // `id` ES el id del documento del candidato en
      // users/{ownerId}/crm_drivers (el listener lo mapea como `id: d.id`), así
      // que se actualiza ESE documento en vez de crear uno nuevo al lado.
      firestoreService
        .onboardDriverToCRM(ownerId, id, {
          candidateId: id,
          name,
          vehicle: assignedVehicleName,
        })
        .catch((e) => {
          console.error('No se pudo sincronizar approveCandidateDriver con Firestore:', e);
        });
    }
  },
  rejectCandidateDriver: (id) => set((state) => ({
    rentCarCandidates: state.rentCarCandidates.filter(c => c.id !== id)
  })),
  resolveReminder: (id) => set((state) => ({
    rentCarReminders: state.rentCarReminders.filter(r => r.id !== id)
  })),
  setActiveChatPartner: (partner) => set({ activeChatPartner: partner }),
  
  toggleRole: () => set((state) => ({ 
    userRole: state.userRole === 'PASSENGER' ? 'DRIVER' : 'PASSENGER' 
  })),
  markFirstRideUsed: () => set({ isFirstRideFreeUsed: true }),
  addStudent: (studentData) => set((state) => {
    const isFirstStudent = state.students.length === 0;
    return {
      students: [...state.students, { ...studentData, id: Math.random().toString(36).substr(2, 9) }],
      // Si estaba buscando contratos y agrega un estudiante, entra a sus 15 días de prueba
      schoolDriverStatus: (isFirstStudent && state.schoolDriverStatus === 'SEARCHING') ? 'TRIAL' : state.schoolDriverStatus
    };
  })
}),
{
  name: 'go-safely-storage-v2',
  storage: createJSONStorage(() => almacenSeguro),
  partialize: (state) => ({
    userRole: state.userRole,
    baseRole: state.baseRole,
    isLoggedIn: state.isLoggedIn,
    userPhoto: state.userPhoto,
    userName: state.userName,
    userPhone: state.userPhone,
    userEmail: state.userEmail,
    userFirebaseUid: state.userFirebaseUid,
    vehicleBrand: state.vehicleBrand,
    vehicleModel: state.vehicleModel,
    vehicleColor: state.vehicleColor,
    vehicleYear: state.vehicleYear,
    licensePlate: state.licensePlate,
    theme: state.theme,
    // Preferencia del APARATO, como el tema: por eso va en partialize y NO
    // en resetSession (ver la regla escrita en la linea ~395). No lleva
    // ningun dato de la persona, asi que no es una fuga al cambiar de
    // usuario; y el chofer que se vuelve a entrar encuentra su radio donde
    // lo dejo. `solicitudesVisibles` NO se persiste a proposito: es de
    // ahora mismo, y guardarlo pintaria «3 solicitudes» al abrir la app sin
    // que haya ninguna.
    radioZonaKm: state.radioZonaKm,
    // FIX SAFETY WALL — Antes isVerified+isDriverVerified vivían SOLO en
    // memoria: al cerrar la app, Zustand rehidrataba sin esos flags y el
    // Safety Wall del map.tsx aparecía aunque el user TUVIERA kycCompleted
    // en Firestore. El user volvía a ver "Comenzar Verificación" como si
    // fuera nuevo. El guard del _layout solo refresca estos flags al pasar
    // por una entry route (/, /auth, etc.) — si la app abre en /(tabs)/map
    // los flags quedan en false hasta que el user navegue manualmente.
    // Persistirlos al storage los mantiene true entre sesiones; el guard
    // los actualiza igual cuando hay cambios reales del lado Firestore.
    isVerified: state.isVerified,
    isDriverVerified: state.isDriverVerified,
    // La vista previa DEBE persistirse. Si no, el revisor cierra la app, la
    // reabre y aparece dentro de un panel de negocio sin el aviso de que
    // está en modo invitado — se lee como que la app le dio acceso real.
    guestPreview: state.guestPreview,
    // Persistir idioma SOLO si el usuario lo cambió manualmente.
    // Si nunca lo tocó, la próxima sesión re-detecta el device locale
    // (por si cambió el idioma del sistema o instaló en otro país).
    ...(state.languageManuallySet ? { language: state.language, languageManuallySet: true } : {}),
  }),
  onRehydrateStorage: () => (state, error) => {
    // ── LA PUERTA TIENE QUE ABRIRSE SIEMPRE ────────────────────────────────
    // `setHasHydrated(true)` vivía DENTRO del `if (state)`. Zustand llama a
    // este callback con `state` indefinido cuando la lectura del disco falla
    // (almacenamiento lleno, datos corruptos de una versión anterior, permisos).
    // En ese caso la puerta no se abría NUNCA y la app se quedaba esperando
    // para siempre, sin error y sin pantalla: exactamente el aspecto de una
    // app colgada. Ahora se abre pase lo que pase; con datos o sin ellos, es
    // mejor arrancar que quedarse mirando.
    if (error) {
      console.warn('[store] no se pudo leer el estado guardado; se arranca limpio:', (error as any)?.message);
    }
    if (state) {
      // MIGRACIÓN — el rol 'GUEST' ya no existe en el código. Quien venga de
      // una instalación vieja lo tiene guardado en disco, y con ese valor la
      // app arranca en un rol que ningún `if` reconoce: cada pantalla decide
      // por descarte y el usuario ve paneles que no le tocan. Lo limpiamos
      // aquí, que es el único punto por el que pasa el estado viejo.
      if ((state.userRole as string) === 'GUEST') {
        state.userRole = null;
        state.baseRole = null;
      }
      state.setHasHydrated(true);
      return;
    }
    // Sin estado (lectura fallida): se abre igual desde el propio store.
    useAppStore.getState().setHasHydrated(true);
  },
}
));
